import invariant from "tiny-invariant"
import { type Address, getAddress } from "viem"
import type { SupportedChainId } from "../config"
import { Currency } from "./Currency"
import { type SerializedToken, tokenSchema } from "./schema"
import type { TokenType } from "./types"

interface TokenArgs {
  chainId?: number | string | undefined
  address: Address
  decimals: number | string
  symbol?: string | undefined
  logoURI?: string | undefined
  name?: string | undefined
  id?: `${SupportedChainId}:${Address}` | undefined
}

/**
 * The `Token` class represents a token-based currency within the application.
 * It extends the `Currency` class and provides specific attributes and functionality for token currencies.
 * Instances of this class are used to work with token-based currencies in a consistent manner.
 */
export class Token extends Currency {
  /**
   * Unique identifier for the token in the format `${chainId}:${address}`.
   * @type {string}
   */
  readonly id: `${SupportedChainId}:${Address}` | Address

  /**
   * Flag to indicate that this currency is not a native blockchain currency.
   * @type {false}
   */
  readonly isNative = false as const

  /**
   * Flag to indicate that this currency is a token-based currency.
   * @type {true}
   */
  readonly isToken = true as const

  /**
   * The contract address on the blockchain where this token is deployed.
   * @type {Address}
   */
  readonly address: Address

  /**
   * The URI of the token logo image.
   * @type {string}
   */
  readonly logoURI?: string | undefined

  /**
   * Constructs a `Token` instance representing a token-based currency.
   * @param {TokenArgs} args - The constructor arguments.
   * @throws {string} - Throws an error if the provided address is not a valid address.
   */
  constructor({ id, chainId, address, logoURI, ...rest }: TokenArgs) {
    super({
      ...rest,
      chainId,
    })
    try {
      this.address = getAddress(address)
      this.id = this.chainId ? id || `${this.chainId}:${address}` : address
      this.logoURI = logoURI
    } catch {
      throw `${address} is not a valid address`
    }
  }

  /**
   * Compares two tokens and determines if this token sorts before the other token based on addresses.
   * @param {Token} other - The other `Token` instance to compare with.
   * @returns {boolean} - True if this token sorts before the other token, false otherwise.
   * @throws {Error} - Throws an error if the chain IDs of the two tokens do not match.
   */
  sortsBefore(other: Token): boolean {
    invariant(this.chainId === other.chainId, "CHAIN_IDS")
    invariant(this.address !== other.address, "ADDRESSES")
    return this.address.toLowerCase() < other.address.toLowerCase()
  }

  /**
   * Checks if this token equals another currency type (Token).
   * @param {TokenType} other - The other currency type to compare with.
   * @returns {boolean} - True if both currencies are equal, false otherwise.
   */
  equals(other: TokenType): boolean {
    return (
      other.isToken &&
      this.chainId === other.chainId &&
      this.address === other.address
    )
  }

  /**
   * Returns the wrapped version of this token.
   * @returns {Token} - The wrapped representation of the token.
   */
  get wrapped(): Token {
    return this
  }

  /**
   * Serializes the `Token` instance into a format suitable for storage or transmission.
   * @returns {SerializedToken} - The serialized representation of the token.
   */
  serialize(): SerializedToken {
    return tokenSchema.parse({
      isNative: this.isNative,
      name: this.name,
      symbol: this.symbol,
      decimals: this.decimals,
      chainId: this.chainId,
      address: this.address,
      logoURI: this.logoURI,
    })
  }

  /**
   * Deserializes a serialized token representation into a `Token` instance.
   * @param {SerializedToken} token - The serialized token representation.
   * @returns {Token} - A `Token` instance representing the token currency.
   */
  static deserialize({
    name,
    symbol,
    address,
    decimals,
    chainId,
    logoURI,
  }: SerializedToken): Token {
    return new Token({
      name,
      symbol,
      address,
      decimals,
      chainId,
      logoURI,
    })
  }
}
